// Libraries
import _ from 'lodash';
import React, {useRef} from 'react';

// Supermove
import {Form, DataLoader, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {
  useModal,
  useNavigationDOM,
  useResponsive,
  useState,
  useEffect,
  useUrlFilters,
} from '@supermove/hooks';
import {
  ClientModel,
  OrganizationModel,
  UserModel,
  Organization,
  ProjectModel,
  JobModel,
  LocationModel,
  Job,
} from '@supermove/models';
import {Json} from '@supermove/utils';

// App
import ContextSwitcher from '@shared/design/components/ContextSwitcher';
import DrawerWithClose from '@shared/design/components/Drawer/DrawerWithClose';
import MultiPaneLayout from '@shared/design/components/Layout/MultiPaneLayout';
import ClientForm from '@shared/modules/Client/forms/ClientForm';
import JobForm from '@shared/modules/Job/forms/JobForm';
import OrganizationKind from '@shared/modules/Organization/enums/OrganizationKind';
import SalespersonCalendar from '@shared/modules/Project/components/SalespersonCalendar';
import SourceKind from '@shared/modules/Project/enums/SourceKind';
import ProjectForm from '@shared/modules/Project/forms/ProjectForm';
import useCreateProjectMutation from '@shared/modules/Project/hooks/useCreateProjectMutation';
import UserRole from '@shared/modules/User/enums/UserRole';
import NewJobSuccessModal from 'modules/App/Job/components/NewJobSuccessModal';
import JobMap from 'modules/Job/components/JobMap';
import NewProjectPageV2Fields from 'modules/Project/New/v2/components/NewProjectPageV2Fields';
import NewProjectPageV2ContentHeader from 'modules/Project/New/v2/components/NewProjectPageV2Header';

const Container = Styled.View`
  flex: 1;
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

type NewProjectPageV2FiltersType = {
  showSalesCalendar: boolean;
};

const getWarehouseLongitudeLatitude = ({form}: {form: Form}) => {
  const warehouseLocationFromForm = _.get(
    form.values,
    `projectForm.jobForms.0.warehouseLocationForm`,
  );

  return warehouseLocationFromForm
    ? ({
        latitude: warehouseLocationFromForm.latitude,
        longitude: warehouseLocationFromForm.longitude,
      } as LocationModel)
    : null;
};

// Form object is expecting the string version of this object.
const getAdditionalItems = ({
  organizationSelectedForCreateProject,
}: {
  organizationSelectedForCreateProject: OrganizationModel;
}) => {
  return Json.toString(
    Organization.getJobFormAdditionalItemsDefaultValues(organizationSelectedForCreateProject),
  );
};

const CONTEXT_OPTIONS = [
  {
    label: 'Map',
    key: 'MAP',
  },
  {
    label: 'Sales Calendar',
    key: 'SALES_CALENDAR',
  },
] as const;

const getJobType = (form: Form, organizationSelectedForCreateProject: OrganizationModel) => {
  const jobTypeId = _.get(form, 'values.projectForm.jobForms.0.jobTypeId');
  const projectTypeId = _.get(form, 'values.projectForm.projectTypeId');
  return Organization.getJobType(organizationSelectedForCreateProject, projectTypeId, jobTypeId);
};

const NewProjectPageV2Content = ({
  viewer,
  client,
  viewingOrganization,
  organizationSelectedForCreateProject,
  initialOrganizationForCreateProject,
  setSlug,
}: {
  viewer: UserModel;
  client: ClientModel;
  viewingOrganization: OrganizationModel;
  organizationSelectedForCreateProject: OrganizationModel;
  initialOrganizationForCreateProject: OrganizationModel;
  slug: string | null;
  setSlug: (slug: string | null) => void;
}) => {
  const responsive = useResponsive();
  const {navigator} = useNavigationDOM();

  const urlFilters = useUrlFilters<NewProjectPageV2FiltersType>({
    getRoute: () => '/projects/new',
    filterKeys: ['showSalesCalendar'],
  });

  const {
    isEnabledMultibranchCreateProjectForAnyOrganizationFromParentAccount,
    isEnabledGooglePlacesCenterLocation,
    isEnabledCreateStorageMultipleWarehouse,
  } = viewingOrganization.features;
  const canEditProjectOrganization = Boolean(
    viewingOrganization.isPrimary &&
      _.includes(UserRole.MANAGER_ROLES_PLUS_SUPER, viewer.role) &&
      isEnabledMultibranchCreateProjectForAnyOrganizationFromParentAccount,
  );
  const primaryOrganization = viewingOrganization.company.organizations.find(
    (organization) => organization.isPrimary,
  );
  const scrollView = useRef<any>(null);
  const mapRef = useRef();
  const modal = useModal();
  const [job, setJob] = useState<JobModel | undefined>(undefined);
  const projectForm = ProjectForm.new(organizationSelectedForCreateProject, {
    creatorId: viewer.id,
    bookedById: viewingOrganization.features.isEnabledNoAutoAssignBookedByToViewer
      ? undefined
      : viewer.id,
    organizationId: initialOrganizationForCreateProject.id,
    primaryLaborSourceOrganizationId: viewingOrganization.id,
    sourceId: organizationSelectedForCreateProject.source.id,
    additionalItems: getAdditionalItems({organizationSelectedForCreateProject}),
    clientForm: client
      ? ClientForm.edit(client)
      : ClientForm.new({
          organizationId: organizationSelectedForCreateProject.id,
          isOkayDuplicatePhoneNumber: true,
        }),
  });

  const {form, submitting, handleSubmit} = useCreateProjectMutation({
    projectForm,
    organization: organizationSelectedForCreateProject,
    // Navigate to the job page on success. We assume there is always one job created when
    // the project is created and thus why [0] works.
    onSuccess: ({project}: {project: ProjectModel}) => {
      setJob(project.jobs[0]);
      modal.handleOpen();
    },
    onError: (errors: unknown) => {
      console.log('errors', errors);
      scrollView.current?.scrollTo({y: 0});
    },
  });

  // The primaryLaborSourceOrganizationId is set by the branch dropdown
  // When the id changes, we update the slug used in the organization query
  // The organizationSelectedForCreateProject is used for all form data
  const primaryLaborSourceOrganizationId = _.get(
    form.values,
    'projectForm.primaryLaborSourceOrganizationId',
  );
  useEffect(() => {
    if (primaryLaborSourceOrganizationId) {
      const selectedBranch = viewingOrganization.company.organizations.find(
        (organization) => _.toString(organization.id) === primaryLaborSourceOrganizationId,
      );
      const slug =
        selectedBranch?.kind !== OrganizationKind.FRANCHISE
          ? primaryOrganization?.slug
          : selectedBranch.slug;
      setSlug(slug || null);
    }
  }, [
    primaryLaborSourceOrganizationId,
    primaryOrganization?.slug,
    setSlug,
    viewingOrganization.company.organizations,
  ]);

  // The warehouseLocation is always pulled from the selected branch (dependent or independent)
  const selectedBranch = viewingOrganization.company.organizations.find(
    (organization) => _.toString(organization.id) === primaryLaborSourceOrganizationId,
  );

  const warehouseLocation = !isEnabledCreateStorageMultipleWarehouse
    ? selectedBranch?.warehouseLocation
    : getWarehouseLongitudeLatitude({form});
  const moveLocations = JobForm.getLocations(_.get(form, 'values.projectForm.jobForms.0'));
  const boundLocations = warehouseLocation ? [warehouseLocation, ...moveLocations] : moveLocations;

  const [selectedContext, setSelectedContext] = useState<(typeof CONTEXT_OPTIONS)[number]['key']>(
    CONTEXT_OPTIONS[0].key,
  );

  const jobType = getJobType(form, organizationSelectedForCreateProject);
  const isEstimateJob = jobType?.kind === Job.KIND.ESTIMATE;
  useEffect(() => {
    if (isEstimateJob) {
      setSelectedContext(CONTEXT_OPTIONS[1].key);
    }
  }, [isEstimateJob]);

  const handleSetUpSalesCalendar = () => {
    navigator.push('/settings/inventory/general');
  };

  return (
    <React.Fragment>
      <Container>
        <MultiPaneLayout
          headerContentComponent={
            <React.Fragment>
              <Space height={24} />
              <NewProjectPageV2ContentHeader
                responsive={responsive}
                handleClose={navigator.goBack}
              />
              <Space height={8} />
            </React.Fragment>
          }
          paneDefinitions={[
            {
              key: 'project-details',
              isScrollable: true,
              style: {
                flex: 1,
              },
              bodyContentComponent: (
                <NewProjectPageV2Fields
                  form={form as Form}
                  scrollViewRef={scrollView}
                  mapRef={mapRef}
                  organizationSelectedForCreateProject={organizationSelectedForCreateProject}
                  submitting={submitting}
                  canEditProjectOrganization={canEditProjectOrganization}
                  isEnabledGooglePlacesCenterLocation={isEnabledGooglePlacesCenterLocation}
                  warehouseLocation={warehouseLocation || null}
                  moveLocations={moveLocations}
                  boundLocations={boundLocations}
                  handleSubmit={handleSubmit}
                  handleShowSalesCalendar={
                    responsive.mobile
                      ? () => {
                          urlFilters.handleUpdate({showSalesCalendar: true});
                        }
                      : undefined
                  }
                />
              ),
            },
            {
              key: 'map-and-calendar',
              tabLabel: 'Map & Calendar',
              bodyContentComponent: (
                <Container>
                  <Row style={{padding: 16}}>
                    <ContextSwitcher
                      isFullWidth
                      contextDefinitions={CONTEXT_OPTIONS.map(({key, ...props}) => {
                        const isSelected = selectedContext === key;
                        return {
                          ...props,
                          isSelected,
                          isDisabled: isSelected,
                          onPress: () => setSelectedContext(key),
                        };
                      })}
                    />
                  </Row>
                  {selectedContext === 'MAP' && (
                    <JobMap
                      locations={moveLocations}
                      boundLocations={boundLocations}
                      warehouseLocation={warehouseLocation}
                    />
                  )}
                  {selectedContext === 'SALES_CALENDAR' && (
                    <Container style={{paddingHorizontal: 16, paddingBottom: 16}}>
                      <SalespersonCalendar
                        organizationSlug={organizationSelectedForCreateProject.slug}
                        jobDate={form.values.projectForm.jobForms[0]?.date}
                        handleSetUpSalesCalendar={handleSetUpSalesCalendar}
                      />
                    </Container>
                  )}
                </Container>
              ),
            },
          ]}
        />
      </Container>
      <NewJobSuccessModal.Content isOpen={modal.isOpen} job={job} onClose={modal.handleClose} />
      <DrawerWithClose
        isOpen={responsive.mobile && urlFilters.getFilters().showSalesCalendar}
        headerText={'Sales Calendar'}
        handleClose={() => {
          urlFilters.handleUpdate({showSalesCalendar: undefined});
        }}
        bodyProps={{
          bodyScrollStyle: {flex: 1},
        }}
      >
        <SalespersonCalendar
          organizationSlug={organizationSelectedForCreateProject.slug}
          jobDate={form.values.projectForm.jobForms[0]?.date}
          handleSetUpSalesCalendar={handleSetUpSalesCalendar}
        />
      </DrawerWithClose>
    </React.Fragment>
  );
};

const NewProjectPageV2 = () => {
  // No slug passed in will default to viewing org, otherwise will read all other fields based on the org from the slug
  const [slug, setSlug] = useState<string | null>(null);
  const {params} = useNavigationDOM();

  const {data, loading} = useQuery<{
    viewer: UserModel;
    client: ClientModel;
    viewingOrganization: OrganizationModel;
    organizationSelectedForCreateProject: OrganizationModel;
  }>(NewProjectPageV2.query, {
    variables: {
      clientUuid: params.clientUuid ? params.clientUuid : null,
      sourceKind: SourceKind.OFFICE_CREATE_PROJECT,
      slug,
    },
  });

  return (
    <DataLoader data={data} loading={loading}>
      {({loadedData}) => {
        return (
          <NewProjectPageV2Content
            viewer={loadedData.viewer}
            client={loadedData.client}
            viewingOrganization={loadedData.viewingOrganization}
            organizationSelectedForCreateProject={loadedData.organizationSelectedForCreateProject}
            initialOrganizationForCreateProject={loadedData.viewer.organizationForCreateProject}
            slug={slug}
            setSlug={setSlug}
          />
        );
      }}
    </DataLoader>
  );
};

NewProjectPageV2.query = gql`
  ${useCreateProjectMutation.fragment}
  ${ClientForm.edit.fragment}
  ${ProjectForm.new.fragment}
  ${Organization.getJobFormAdditionalItemsDefaultValues.fragment}
  ${Organization.getJobType.fragment}
  ${NewProjectPageV2Fields.fragment}

  query NewProjectPageV2($clientUuid: String, $sourceKind: String!, $slug: String) {
    ${gql.query}
    viewer {
      id
      role
      organizationForCreateProject {
        id
        slug
      }
    }
    client(clientUuid: $clientUuid) {
      id
      ...ClientForm_edit
    }
    viewingOrganization: organization {
      id
      isPrimary
      slug
      company {
        id
        organizations {
          id
          name
          slug
          kind
          isPrimary
          warehouseLocation {
            latitude
            longitude
          }
        }
      }
      features {
        isEnabledMultibranchCreateProjectForAnyOrganizationFromParentAccount: isEnabled(
          feature: "MULTIBRANCH_CREATE_PROJECT_FOR_ANY_ORGANIZATION_FROM_PARENT_ACCOUNT"
        )
        isEnabledGooglePlacesCenterLocation: isEnabled(feature: "GOOGLE_PLACES_CENTER_LOCATION")
        isEnabledCreateStorageMultipleWarehouse: isEnabled(feature: "CREATE_STORAGE_MULTIPLE_WAREHOUSE")
        isEnabledNoAutoAssignBookedByToViewer: isEnabled(feature: "NO_AUTO_ASSIGN_BOOKED_BY_TO_VIEWER")
      }
    }
    organizationSelectedForCreateProject: organization(slug: $slug) {
      id
      name
      source(sourceKind: $sourceKind) {
        id
      }
      ...useCreateProjectMutation
      ...ProjectForm_new
      ...Organization_getJobFormAdditionalItemsDefaultValues
      ...Organization_getJobType
      ...NewProjectPageV2Fields
    }
  }
`;

export default NewProjectPageV2;
